import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useMediaQuery } from "./useMediaQuery";
import { useSidebarStore } from "../sidebarStore";
import { SIDEBAR_KEYBOARD_SHORTCUT } from "../constants";
import { SidebarContextValue, SidebarState } from "../types";

/**
 * Hook to access and manage sidebar state
 * Uses Zustand for global state management
 */
export function useSidebar(): SidebarContextValue {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const location = useLocation();

  const open = useSidebarStore((s) => s.expanded);
  const setExpanded = useSidebarStore((s) => s.setExpanded);
  const hoverExpanded = useSidebarStore((s) => s.hoverExpanded);
  const setHoverExpanded = useSidebarStore((s) => s.setHoverExpanded);
  // Hover-to-expand is a pointer affordance; on touch devices the rail stays a
  // rail until it is toggled.
  const canHover = useMediaQuery("(hover: hover)");

  const prevIsMobileRef = useRef(isMobile);

  // Wrapper for setOpen to handle state (persist middleware handles localStorage automatically)
  const setOpen = useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      setExpanded(openState);
    },
    [open, setExpanded],
  );

  // Toggle sidebar - uses the shared open state
  const toggleSidebar = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);
  // Keyboard shortcut to toggle sidebar (Cmd/Ctrl + B)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === SIDEBAR_KEYBOARD_SHORTCUT
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  // Close sidebar when switching to mobile, restore when switching to desktop
  useEffect(() => {
    if (prevIsMobileRef.current !== isMobile) {
      if (isMobile) {
        // Switching to mobile: close sidebar
        setExpanded(false);
      } else {
        // Switching to desktop: restore persisted state (persist middleware handles localStorage)
        const persistedState =
          localStorage.getItem("SIDEBAR_EXPANDED") !== "false";
        setExpanded(persistedState);
      }
      prevIsMobileRef.current = isMobile;
    }
  }, [isMobile, setExpanded]);

  // The collapsed rail expands as an overlay while the pointer is over it. It
  // never applies to the pinned-open sidebar (nothing to expand) or to mobile,
  // where the sidebar is offcanvas.
  const isHoverExpanded = hoverExpanded && !open && !isMobile && canHover;

  // Drop a stale hover expansion when the pointer can no longer be over the
  // rail — e.g. the layout switched to mobile, or navigation unmounted the
  // sidebar before `mouseleave` fired.
  useEffect(() => {
    if (hoverExpanded && (isMobile || !canHover)) {
      setHoverExpanded(false);
    }
  }, [hoverExpanded, isMobile, canHover, setHoverExpanded]);

  const state: SidebarState =
    open || isHoverExpanded ? "expanded" : "collapsed";

  return {
    state,
    open,
    setOpen,
    isMobile,
    toggleSidebar,
    isHoverExpanded,
    setHoverExpanded,
  };
}
